import React, { useCallback, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Headphones,
  Info,
  Maximize,
  Mic,
  Monitor,
  RefreshCw,
  RotateCcw,
  Shield,
  Video,
  Wifi,
  XCircle,
} from "lucide-react";
import { Lead, MonitoringConfig, MonitoringState, ViolationType } from "@/types/video-call";
import { useFullscreen } from "@/hooks/video-call/useFullscreen";
import { useSpeechMicTest } from "@/hooks/video-call/useSpeechMicTest";
import { useEarphoneDetection } from "@/hooks/video-call/useEarphoneDetection";
import { useDeviceEnumeration } from "@/hooks/video-call/useDeviceEnumeration";
import { useSpeedTest } from "@/hooks/video-call/useSpeedTest";
import { DeviceSelector } from "./DeviceSelector";
import { InterviewReadyScreen } from "./InterviewReadyScreen";
import * as Sentry from "@sentry/react";

interface UnifiedVideoCallSetupProps {
  lead: Lead;
  config: MonitoringConfig;
  monitoringState: MonitoringState;
  preparingCall: boolean;
  onStartCall: (skipPermissionCheck?: boolean) => void;
  onNavigateBack: () => void;
  onRequestPermissions: () => Promise<boolean>;
  onRetryMonitorDetection?: () => Promise<boolean>;
  isInterviewSession?: boolean;
  interviewDetails?: any;
  selectedAssistantId?: string;
  onAssistantSelection?: () => void;
  callActive?: boolean;
  onAddViolation?: (type: ViolationType, message: string, severity: 'warning' | 'penalty' | 'critical') => void;
  onDeviceSelectionChange?: (devices: { videoDeviceId?: string; audioDeviceId?: string; audioOutputDeviceId?: string }) => void;
}

export const UnifiedVideoCallSetup: React.FC<UnifiedVideoCallSetupProps> = ({
  lead,
  config,
  monitoringState,
  preparingCall,
  onStartCall,
  onNavigateBack,
  onRequestPermissions,
  onRetryMonitorDetection,
  isInterviewSession = false,
  interviewDetails = null,
  selectedAssistantId = "",
  onAssistantSelection,
  callActive = false,
  onAddViolation,
  onDeviceSelectionChange,
}) => {
  const { toast } = useToast();
  const [requestingPermissions, setRequestingPermissions] = useState(false);
  const [requestingFullscreen, setRequestingFullscreen] = useState(false);
  const [fullscreenAttempted, setFullscreenAttempted] = useState(false);
  const [allPermissionsGranted, setAllPermissionsGranted] = useState(false);
  const [showInterviewReadyScreen, setShowInterviewReadyScreen] = useState(false);
  const [earphoneAcknowledged, setEarphoneAcknowledged] = useState(false);
  const [earphoneTestPassed, setEarphoneTestPassed] = useState(false);
  const [earphoneAttemptCount, setEarphoneAttemptCount] = useState(0);
  const [earphoneTestBypassed, setEarphoneTestBypassed] = useState(false);
  const [showEarphoneModal, setShowEarphoneModal] = useState(false);
  const [speedTestAttempted, setSpeedTestAttempted] = useState(false);

  // Ref for device selection section
  const deviceSelectionRef = React.useRef<HTMLDivElement>(null);
  
  // Web Speech API microphone test
  const { result: speechMicResult, testMicWithSpeech, resetTest } = useSpeechMicTest();
  
  // Earphone detection test
  const { result: earphoneResult, testEarphoneConnection, resetTest: resetEarphoneTest } = useEarphoneDetection();

  // Speed test
  const { state: speedTestState, runSpeedTest } = useSpeedTest();

  // Device enumeration for device selection
  const deviceEnumeration = useDeviceEnumeration();

  // Test results state
  const [micTestResult, setMicTestResult] = useState<any>(null);
  const [speakerTestResult, setSpeakerTestResult] = useState<any>(null);

  // Auto-run speed test once on mount
  useEffect(() => {
    setSpeedTestAttempted(true);
    runSpeedTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speedTestPassed = speedTestState.result?.meetsMinimumRequirement ?? false;

  // Notify parent component when device selection changes
  useEffect(() => {
    if (onDeviceSelectionChange) {
      onDeviceSelectionChange(deviceEnumeration.selectedDevices);
    }
  }, [deviceEnumeration.selectedDevices, onDeviceSelectionChange]);

  // Test selected microphone device
  const handleTestSelectedMicrophone = useCallback(async () => {
    setMicTestResult(null);
    const result = await deviceEnumeration.testMicrophone(deviceEnumeration.selectedDevices.audioDeviceId);
    setMicTestResult(result);
    
    if (result.success) {
      toast({
        title: "🎤 Microphone Test Passed",
        description: `Audio input detected successfully! Volume level: ${Math.round(result.volume || 0)}`,
      });
    } else {
      toast({
        title: "🎤 Microphone Test Failed", 
        description: result.error || "No audio input detected",
        variant: "destructive",
      });
    }
  }, [deviceEnumeration, toast]);

  // Test speaker function
  const handleTestSpeaker = useCallback(async () => {
    setSpeakerTestResult(null);
    const result = await deviceEnumeration.testSpeaker(deviceEnumeration.selectedDevices.audioOutputDeviceId);
    setSpeakerTestResult(result);
    
    if (result.success) {
      toast({
        title: "🔊 Speaker Test Completed",
        description: "Test tone played successfully. Did you hear it through your selected speakers?",
      });
    } else {
      toast({
        title: "🔊 Speaker Test Failed",
        description: result.error || "Failed to play test tone",
        variant: "destructive", 
      });
    }
  }, [deviceEnumeration, toast]);

  const handleRerunSpeedTest = useCallback(() => {
    runSpeedTest();
  }, [runSpeedTest]);

  // Refresh devices manually
  const handleRefreshDevices = useCallback(async () => {
    await deviceEnumeration.refreshDevices();
    toast({
      title: "🔄 Devices Refreshed",
      description: "Device list has been updated with any newly connected devices.",
    });
  }, [deviceEnumeration, toast]);

  // Enhanced fullscreen hook with security monitoring
  const { requestFullscreen, isSupported: fullscreenSupported, isFullscreen, forceExitCheck } = useFullscreen({
    enableViolationTracking: isInterviewSession,
    isInterviewActive: callActive,
    onFullscreenChange: (isFullscreen, eventType) => {
      
      // Re-verify monitor detection when exiting fullscreen during interview
      if (!isFullscreen && callActive && config.externalMonitorDetection && eventType !== 'enter') {
        setTimeout(() => {
          onRetryMonitorDetection?.();
        }, 500); // Small delay to let fullscreen transition complete
      }
      
      // Notify if tab change or blur monitoring needs to be re-initialized
      if (eventType === 'exit' || eventType === 'escape_key') {
        // Force re-check of all monitoring systems
        setTimeout(() => {
          forceExitCheck();
        }, 1000);
      }
    },
    onSecurityViolation: (type, details) => {
      const severity = type === 'escape_key_used' ? 'warning' : 'critical';
      const message = type === 'escape_key_used' 
        ? 'ESC key used to exit fullscreen during interview'
        : 'Unauthorized fullscreen exit detected during interview';
      
      toast({
        title: "Security Notice",
        description: message,
        variant: severity === 'critical' ? 'destructive' : 'default',
      });
    },
    onAddViolation: (type, message, severity) => {
      onAddViolation?.(type, message, severity);
    }
  });

  const checkInitialChecksCompleted = useCallback(() => {
    const screenShareOk = !config.screenSharingEnabled || monitoringState.permissionStatus.screenShare === "granted";
    const monitorCheckOk = !config.externalMonitorDetection || monitoringState.permissionStatus.monitorCheck === "passed";
    return screenShareOk && monitorCheckOk;
  }, [monitoringState.permissionStatus, config.screenSharingEnabled, config.externalMonitorDetection]);

  const getRemainingChecksMessage = useCallback(() => {
    const cameraOk = monitoringState.permissionStatus.camera === "granted";
    const micOk = speechMicResult.status === 'working';
    const earphoneOk = earphoneResult.status === 'earphones_detected' || earphoneTestPassed || earphoneTestBypassed;

    const cameraMicDone = cameraOk && micOk;
    const earphoneDone = earphoneOk;

    if (cameraMicDone && !earphoneDone) {
      return "Please test your earphones to proceed with the interview.";
    }
    if (!cameraMicDone && earphoneDone) {
      return "Please test your microphone to proceed with the interview.";
    }
    if (!cameraMicDone && !earphoneDone) {
      return "Once the microphone, and earphones are connected, kindly test them to proceed with the interview";
    }
    if (!speedTestPassed) {
      if (speedTestState.isRunning) return "Checking your internet speed, please wait...";
      if (speedTestState.stage === 'error') return "Internet speed test failed. Please use the Re-test button to try again.";
      if (speedTestState.result) return `Your internet speed does not meet the minimum requirements (10 Mbps ↓ / 3 Mbps ↑). Please improve your connection and re-test.`;
    }
    return null;
  }, [monitoringState.permissionStatus.camera, speechMicResult.status, earphoneResult.status, earphoneTestPassed, earphoneTestBypassed, speedTestPassed, speedTestState.isRunning, speedTestState.stage, speedTestState.result]);

  // Check if all permissions are granted (earphone test is mandatory)
  const checkAllPermissions = useCallback(() => {
    const cameraOk = monitoringState.permissionStatus.camera === "granted";
    const micOk = speechMicResult.status === 'working';
    const screenShareOk = !config.screenSharingEnabled || monitoringState.permissionStatus.screenShare === "granted";
    const monitorCheckOk = !config.externalMonitorDetection || monitoringState.permissionStatus.monitorCheck === "passed";
    // Earphone test is mandatory - passes after actual detection OR if user bypassed after 2+ attempts
    const earphoneOk = earphoneResult.status === 'earphones_detected' || earphoneTestPassed || earphoneTestBypassed;

    return cameraOk && micOk && screenShareOk && monitorCheckOk && earphoneOk && speedTestPassed;
  }, [monitoringState.permissionStatus, speechMicResult.status, config.screenSharingEnabled, config.externalMonitorDetection, earphoneResult.status, earphoneTestPassed, earphoneTestBypassed, earphoneAttemptCount, speedTestPassed]);

  // Auto-detect permission changes and reload page
  useEffect(() => {
    let permissionWatchers: (() => void)[] = [];

    const watchPermissions = async () => {
      if ('permissions' in navigator && monitoringState.permissionStatus.camera === 'denied') {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });

          const handlePermissionChange = () => {
            // If permissions were previously denied and now both are granted, reload
            if (cameraPermission.state === 'granted' && micPermission.state === 'granted') {
              console.log('📹 Permissions granted, reloading page to apply changes...');
              setTimeout(() => {
                window.location.reload();
              }, 500); // Small delay to ensure permissions are fully applied
            }
          };

          cameraPermission.addEventListener('change', handlePermissionChange);
          micPermission.addEventListener('change', handlePermissionChange);

          // Store cleanup functions
          permissionWatchers.push(() => {
            cameraPermission.removeEventListener('change', handlePermissionChange);
            micPermission.removeEventListener('change', handlePermissionChange);
          });
        } catch (error) {
          console.log('Permissions API not supported or failed:', error);
        }
      }
    };

    watchPermissions();

    return () => {
      permissionWatchers.forEach(cleanup => cleanup());
    };
  }, [monitoringState.permissionStatus.camera]);

  // Update permission status when monitoring state changes
  useEffect(() => {
    const permissionsGranted = checkAllPermissions();
    setAllPermissionsGranted(permissionsGranted);

    // Show interview ready screen when all permissions are granted for interview sessions
    if (permissionsGranted && isInterviewSession && !showInterviewReadyScreen) {
      setShowInterviewReadyScreen(true);

      // Log final earphone status when all permissions are granted
      const earphoneStatus = earphoneResult.status === 'earphones_detected' || earphoneTestPassed
        ? 'PASSED'
        : earphoneTestBypassed
        ? 'BYPASSED'
        : 'UNKNOWN';

      console.log('🎧 FINAL EARPHONE STATUS:', {
        status: earphoneStatus,
        testPassed: earphoneTestPassed,
        testBypassed: earphoneTestBypassed,
        totalAttempts: earphoneAttemptCount,
        deepgramStatus: earphoneResult.status,
        allPermissionsGranted: true,
        timestamp: new Date().toISOString()
      });
    }
  }, [checkAllPermissions, isInterviewSession, showInterviewReadyScreen, earphoneResult.status, earphoneTestPassed, earphoneTestBypassed, earphoneAttemptCount]);


  // Extract values for use in JSX
  const cameraGranted = monitoringState.permissionStatus.camera === "granted";
  const micWorking = speechMicResult.status === 'working';
  const micMuted = speechMicResult.status === 'muted';
  const micTesting = speechMicResult.isChecking;



  // Convert monitoring state to new UI format
  const buildPermissionItems = useCallback(() => {
    const items = [];
    
    const getMicDescription = () => {
      if (micTesting) {
        switch (speechMicResult.testPhase) {
          case 'baseline':
            return "Setting up microphone test... Please stay quiet for a moment";
          case 'speech_test':
            return `🗣️ Please say "Hi, can you hear me?" clearly into your microphone`;
          default:
            return "🗣️ Testing microphone... Please say 'Hi, can you hear me?'";
        }
      }
      if (!cameraGranted) return "Video and audio permissions required for real-time communication";
      if (micMuted) return "No audio detected - click 'Test Mic' button to try again";
      if (micWorking) return "Camera and microphone ready ✅";
      if (speechMicResult.status === 'error') return "Microphone test failed - please try again";
      return "We'll need access to your camera and microphone for real-time communication.";
    };
    
    // Microphone inline error message
    const getMicInlineError = () => {
      if (micTesting || micWorking) return null;
      
      // Show permission denied message immediately when permissions are denied
      if (monitoringState.permissionStatus.camera === "denied") {
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-danger">
                  <strong>Camera & Microphone Permission Manual Reset Required</strong>
                </p>
                <ul className="text-sm text-danger list-disc list-inside space-y-1 ml-3">
                  <li>Click the camera/site settings icon in your browser's address bar (left of the URL)</li>
                  <li>Select "Allow" for both Camera and Microphone permissions</li>
                  <li>Refresh the page and try again</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-danger bg-card p-2 rounded">
              <strong>Alternative:</strong> Go to Chrome Settings → Privacy and security → Site settings → Camera/Microphone and allow this site.
            </p>
          </div>
        );
      }
      
      if (!cameraGranted) return null;
      
      if (speechMicResult.status === 'muted') {
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-danger">
                  No audio detected during mic test.
                </p>
                <p className="text-sm text-danger">
                  Possible reasons:
                </p>
                <ul className="text-sm text-danger list-disc list-inside space-y-1 ml-3">
                  <li>Mic is muted or disconnected</li>
                  <li>Input volume too low</li>
                  <li>Wrong mic selected</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-mid bg-card p-2 rounded">
              <strong>💡 Tip:</strong> Check your device settings, select the correct microphone below, then click "Test Mic" again.
            </p>
          </div>
        );
      }
      if (speechMicResult.status === 'error') {
        return (
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium text-danger">Microphone test failed. Check Device Selection below for correct microphone, then try again.</p>
          </div>
        );
      }
      
      return null;
    };

    // Camera & Microphone (always present)
    items.push({
      title: "Camera & Microphone",
      description: getMicDescription(),
      status: cameraGranted && micWorking ? "granted" : 
              monitoringState.permissionStatus.camera === "denied" || micMuted || speechMicResult.status === 'error' ? "error" : "pending",
      isError: monitoringState.permissionStatus.camera === "denied" || micMuted || speechMicResult.status === 'error' ||
               (cameraGranted && speechMicResult.status !== 'working' && speechMicResult.status !== 'testing'),
      isTesting: micTesting,
      inlineError: getMicInlineError()
    });
    
    // Screen sharing inline error message
    const getScreenShareInlineError = () => {
      if (!config.screenSharingEnabled) return null;
      
      if (monitoringState.permissionStatus.screenShare === "denied") {
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-danger">
                  <strong>Screen Sharing Validation Failed</strong>
                </p>
                <p className="text-sm text-danger">
                  You must share your entire screen for proctoring compliance. Window or tab-only sharing won't work.
                </p>
              </div>
            </div>
            <p className="text-sm text-danger bg-card p-2 rounded">
              <strong>Action Required:</strong> Click the "Retry" button above and select "Share entire screen" when prompted.
            </p>
          </div>
        );
      }
      
      return null;
    };

    // Screen Sharing (if enabled)
    if (config.screenSharingEnabled) {
      items.push({
        title: "Screen Sharing",
        description: "You'll be asked to share your entire screen. Window or tab-only sharing won't work.",
        status: monitoringState.permissionStatus.screenShare === "granted" ? "granted" : 
                monitoringState.permissionStatus.screenShare === "denied" ? "error" : "pending",
        isError: monitoringState.permissionStatus.screenShare === "denied",
        inlineError: getScreenShareInlineError()
      });
    }
    
    // Monitor detection inline error message
    const getMonitorInlineError = () => {
      if (!config.externalMonitorDetection) return null;
      
      if (monitoringState.permissionStatus.monitorCheck === "failed") {
        return (
          <div className="space-y-3">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium text-danger">
                  <strong>Multiple Monitors Detected</strong>
                </p>
                <p className="text-sm text-danger">
                  Multiple displays detected on your system. For proctoring compliance, please:
                </p>
                <ul className="text-sm text-danger list-disc list-inside space-y-1 ml-3">
                  <li>Disconnect all external monitors (HDMI, USB-C, wireless displays)</li>
                  <li>Turn off screen mirroring and extended desktop mode</li>
                  <li>Close virtual display software</li>
                  <li>Unplug any display adapters or docking stations</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-danger bg-card p-2 rounded">
              <strong>Next Step:</strong> After disconnecting external displays, click the "Retry" button above to check again.
            </p>
          </div>
        );
      }
      
      return null;
    };

    // Monitor Detection (if enabled)
    if (config.externalMonitorDetection) {
      items.push({
        title: "Single Monitor",
        description: "If you're using more than one monitor, please disconnect the extras before continuing.",
        status: monitoringState.permissionStatus.monitorCheck === "passed" ? "granted" : 
                monitoringState.permissionStatus.monitorCheck === "failed" ? "error" : "pending",
        isError: monitoringState.permissionStatus.monitorCheck === "failed",
        inlineError: getMonitorInlineError()
      });
    }
    
    // Earphones Requirement (always present)
    const getEarphoneDescription = () => {
      if (earphoneResult.isChecking) {
        switch (earphoneResult.testPhase) {
          // Deepgram test phases
          case 'setup':
            return "🔧 Setting up audio verification service...";
          case 'connecting':
            return "🌐 Connecting to audio verification service...";
          case 'listening':
            return "🎤 Listening for audio feedback...";
          case 'playing':
            return "🔊 Playing test phrase... (you should hear: 'Testing audio one two three four five')";
          case 'analyzing':
            return "🔍 Analyzing audio feedback for earphone detection...";
          // Legacy phases for compatibility
          case 'playing_audio':
            return "🔊 Playing test phrase... (you should hear: 'Testing audio one two three four five')";
          case 'transcribing':
            return "🎤 Listening for audio leakage...";
          case 'baseline':
            return "🔊 Measuring background audio levels...";
          case 'tone_test':
            return "🔊 Playing test tones to detect echo... (you should hear 3 brief tones)";
          case 'analysis':
            return "🔍 Analyzing audio feedback...";
          case 'complete':
            return "✅ Audio verification completed";
          default:
            return "🎧 Testing earphones...";
        }
      }
      if (earphoneResult.status === 'speakers_detected') {
        return "🚫 External speakers detected - Earphones are required for interview security";
      }
      if (earphoneResult.status === 'earphones_detected' || earphoneTestPassed) {
        return "✅ Earphones verified - Interview security requirements met";
      }
      if (earphoneTestBypassed) {
        return "⚠️ Earphone test bypassed - Please ensure proper audio setup for the best experience";
      }
      if (earphoneResult.status === 'inconclusive') {
        // Check if it's a low volume issue
        if (earphoneResult.error && (earphoneResult.error.includes('too quiet') || earphoneResult.error.includes('volume'))) {
          return "🔊 Audio too quiet - Please increase your speaker/system volume to maximum and test again";
        }
        return "❓ Unable to verify earphone connection - Please ensure earphones are connected and test again";
      }
      if (earphoneResult.status === 'error') {
        // Show specific error messages
        if (earphoneResult.error) {
          if (earphoneResult.error.includes('API key')) {
            return "❌ Audio verification service unavailable - Please contact support";
          }
          if (earphoneResult.error.includes('network') || earphoneResult.error.includes('connection') || earphoneResult.error.includes('WebSocket')) {
            return "❌ Connection failed - Please check your internet connection and try again";
          }
        }
        return "❌ Audio verification failed - Please check your earphone connection and test again";
      }
      return "For best audio quality, please use wired earphones or Bluetooth earbuds. External speakers will not work.";
    };

    // Earphone inline error message
    const getEarphoneInlineError = () => {
      if (!cameraGranted || earphoneResult.isChecking || earphoneResult.status === 'earphones_detected' || earphoneTestPassed || earphoneTestBypassed) {
        return null;
      }
      
      if (earphoneAttemptCount > 0) {
        if (earphoneResult.status === 'speakers_detected') {
          return (
            <div className="space-y-3">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-danger">
                  🚫<strong>External speakers detected!</strong> For interview security, earphones are mandatory.
                  </p>
                  <ul className="text-sm text-danger list-disc list-inside space-y-1 ml-3">
                    <li>Connect wired earphones, headphones, or Bluetooth earbuds</li>
                    <li>Turn off or disconnect all external speakers</li>
                  </ul>
                </div>
              </div>
              <p className="text-sm font-medium text-danger bg-card p-2 rounded">
                <strong>Then:</strong> Check Device Selection below for correct speaker output device and test again.
              </p>
            </div>
          );
        }
        if (earphoneResult.status === 'inconclusive') {
          return (
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium text-danger">Unable to verify earphone connection. Check Device Selection below for correct audio devices, ensure earphones are connected, then test again.</p>
            </div>
          );
        }
        if (earphoneResult.status === 'error') {
          if (earphoneResult.error?.includes('API key')) {
            return (
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-danger">Verification service unavailable. Please contact support.</p>
              </div>
            );
          }
          if (earphoneResult.error?.includes('network') || earphoneResult.error?.includes('connection')) {
            return (
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium text-danger">Connection failed. Check internet and try again.</p>
              </div>
            );
          }
          return (
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium text-danger">Earphone verification failed. Check Device Selection below for correct audio devices and try again.</p>
            </div>
          );
        }
      }
      
      return null;
    };

    items.push({
      title: "Earphones (Required)",
      description: getEarphoneDescription(),
      status: (earphoneResult.status === 'earphones_detected' || earphoneTestPassed || earphoneTestBypassed) ? "granted" : 
              (earphoneResult.status === 'speakers_detected' || earphoneResult.status === 'inconclusive' || earphoneResult.status === 'error') ? "error" : "pending",
      isError: (earphoneResult.status === 'speakers_detected' || earphoneResult.status === 'inconclusive' || earphoneResult.status === 'error') || 
               (cameraGranted && !earphoneResult.isChecking && earphoneResult.status !== 'earphones_detected' && !earphoneTestPassed && !earphoneTestBypassed),
      requiresCheckbox: false, // No checkbox needed - green tick shows success
      requiresTest: true,
      isTesting: earphoneResult.isChecking,
      inlineError: getEarphoneInlineError()
    });

    // Internet Speed (always present)
    const getSpeedDescription = () => {
      if (speedTestState.isRunning) {
        if (speedTestState.stage === 'downloading') return "Testing download speed...";
        if (speedTestState.stage === 'uploading') return "Testing upload speed...";
        return "Checking your internet connection speed...";
      }
      if (speedTestState.stage === 'complete' && speedTestState.result) {
        const { downloadSpeed, uploadSpeed } = speedTestState.result;
        if (speedTestState.result.meetsMinimumRequirement) {
          return `✅ Speed verified: ${downloadSpeed} Mbps ↓ / ${uploadSpeed} Mbps ↑ — meets requirements`;
        }
        return `Detected: ${downloadSpeed} Mbps ↓ / ${uploadSpeed} Mbps ↑ — below requirements`;
      }
      if (speedTestState.stage === 'error') {
        return "Speed test failed. Please use the Re-test button to check your connection.";
      }
      return "Checking your internet speed to ensure a smooth interview experience...";
    };

    const getSpeedInlineError = () => {
      if (speedTestState.stage === 'complete' && speedTestState.result && !speedTestState.result.meetsMinimumRequirement) {
        const { downloadSpeed, uploadSpeed } = speedTestState.result;
        return (
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-danger">
                Your connection does not meet the minimum requirements. Please check your connection and try again.
              </p>
              <div className="text-sm text-danger mt-1 space-y-0.5">
                <p>Download: <strong>{downloadSpeed} Mbps</strong> {downloadSpeed < 10 ? <span className="text-danger">(need ≥ 10 Mbps)</span> : <span className="text-good">✓</span>}</p>
                <p>Upload: <strong>{uploadSpeed} Mbps</strong> {uploadSpeed < 3 ? <span className="text-danger">(need ≥ 3 Mbps)</span> : <span className="text-good">✓</span>}</p>
              </div>
            </div>
          </div>
        );
      }
      if (speedTestState.stage === 'error') {
        return (
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium text-danger">
              Speed test failed. Please check your internet connection and try again.
            </p>
          </div>
        );
      }
      return null;
    };

    items.push({
      title: "Internet Speed",
      description: getSpeedDescription(),
      status: speedTestPassed ? "granted" :
              (speedTestState.stage === 'complete' || speedTestState.stage === 'error') ? "error" : "pending",
      isError: (speedTestState.stage === 'complete' && !speedTestPassed) || speedTestState.stage === 'error',
      isTesting: speedTestState.isRunning,
      requiresTest: true,
      requiresCheckbox: false,
      canRetest: true,
      inlineError: getSpeedInlineError(),
    });

    return items;
  }, [monitoringState.permissionStatus, speechMicResult, config.screenSharingEnabled, config.externalMonitorDetection, cameraGranted, micWorking, micMuted, micTesting, earphoneAcknowledged, earphoneResult, earphoneTestPassed, earphoneTestBypassed, earphoneAttemptCount, speedTestState, speedTestPassed]);

  const getStatusIcon = (status: string, isError?: boolean, onRetry?: () => void, isCameraPermission?: boolean) => {
    if (status === "granted") {
      return(
        <div className="w-8 h-8 bg-[#E9F9EF] rounded-xl flex items-center justify-center">  
          <Check className="w-5 h-5 text-[#0C7436]" />
        </div>
      );
    }
    if (isError) {
      // For camera/microphone/earphone permissions, show info icon instead of retry button
      if (isCameraPermission) {
        return (
          <div className="w-8 h-8 bg-[#FEF1E8] rounded-xl flex items-center justify-center" title="Use Test Button">
            <Info className="w-4 h-4 text-[#E36914]" />
          </div>
        );
      }
      // For other permissions (screen share, monitor), show retry button
      return (
        <button
          onClick={onRetry}
          disabled={requestingPermissions || preparingCall}
          className="w-8 h-8 bg-danger-bg rounded-xl flex items-center justify-center hover:bg-danger-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Retry"
        >
          <RotateCcw className="w-4 h-4 text-danger" />
        </button>
      );
    }
    return null;
  };

  const handleRetryPermissions = useCallback(async () => {
    setRequestingPermissions(true);
    try {
      const permissionsGranted = await onRequestPermissions();
      if (!permissionsGranted) {
        toast({
          title: "Permissions Required",
          description: "Please grant all required permissions to continue",
          variant: "destructive",
        });
        return false;
      }
      return true;
    } catch (error) {
      Sentry.logger.error("Error retrying permissions", { error: error, component: 'UnifiedVideoCallSetup' });
      toast({
        title: "Error",
        description: "Failed to request permissions. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setRequestingPermissions(false);
    }
  }, [onRequestPermissions, toast]);

  const handleTestMicrophone = useCallback(async () => {
    try {
      // First ensure we have camera permissions
      const permissionsGranted = await handleRetryPermissions();
      if (!permissionsGranted) {
        toast({
          title: "Permissions Required",
          description: "Please grant camera and microphone access first to test your microphone",
          variant: "destructive",
        });
        return;
      }

      // Reset any previous test results
      resetTest();
      
      // Start the microphone test
      const micWorking = await testMicWithSpeech(deviceEnumeration.selectedDevices.audioDeviceId);
      
      if (micWorking) {
        toast({
          title: "Microphone Test Passed",
          description: "Your microphone is working correctly! You can now proceed with the interview.",
        });
      } else {
        // Auto-scroll to device selection when mic test fails
        setTimeout(() => {
          if (deviceSelectionRef.current) {
            deviceSelectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 500); // Small delay to let the inline error render first
      }
    } catch (error) {
      Sentry.logger.error('Microphone test error', { error: error, component: 'UnifiedVideoCallSetup' });
      toast({
        title: "Test Error", 
        description: "An error occurred while testing your microphone. Please try again.",
        variant: "destructive",
      });
    }
  }, [handleRetryPermissions, testMicWithSpeech, resetTest, toast, deviceEnumeration.selectedDevices.audioDeviceId]);

  const handleTestEarphones = useCallback(async () => {
    // First ensure we have camera permissions for audio access
    const permissionsGranted = monitoringState.permissionStatus.camera === "granted";
    if (!permissionsGranted) {
      toast({
        title: "Permissions Required",
        description: "Please grant camera and microphone access first to test earphones",
        variant: "destructive",
      });
      return;
    }

    // Reset test state when opening modal for fresh test
    resetEarphoneTest();
    
    // Open the earphone test modal
    setShowEarphoneModal(true);
  }, [monitoringState.permissionStatus.camera, toast, resetEarphoneTest]);

  const handleEarphoneModalTest = useCallback(async () => {
    try {
      // Increment attempt count and start the test
      const newAttemptCount = earphoneAttemptCount + 1;
      setEarphoneAttemptCount(newAttemptCount);

      // Reset previous earphone test at start of new test
      resetEarphoneTest();
      setEarphoneTestPassed(false);
      
      // Start the earphone detection test
      const isUsingEarphones = await testEarphoneConnection(
        deviceEnumeration.selectedDevices.audioDeviceId,
        deviceEnumeration.selectedDevices.audioOutputDeviceId
      );
      
      if (isUsingEarphones) {
        // Test passed - close modal and update state
        console.log('🎧 EARPHONE TEST PASSED', {
          attemptCount: newAttemptCount,
          status: earphoneResult.status,
          confidence: earphoneResult.confidence,
          echoLevel: earphoneResult.echoLevel,
          timestamp: new Date().toISOString()
        });
        
        setEarphoneTestPassed(true);
        setEarphoneAcknowledged(true);
        setShowEarphoneModal(false);
        toast({
          title: "Earphones Verified ✓",
          description: "Excellent! Interview security requirements have been met.",
        });
      } else {
        // Test failed - log the failure
        console.log('🎧 EARPHONE TEST FAILED', {
          attemptCount: newAttemptCount,
          status: earphoneResult.status,
          error: earphoneResult.error,
          confidence: earphoneResult.confidence,
          canBypass: newAttemptCount >= 2,
          retryLimitReached: newAttemptCount >= 2,
          timestamp: new Date().toISOString()
        });
      }
      // If test fails, modal stays open for retry - no else block needed
    } catch (error) {
      // Keep modal open on error for retry
      console.log('🎧 EARPHONE TEST ERROR', {
        attemptCount: earphoneAttemptCount + 1,
        error: error.message || error,
        timestamp: new Date().toISOString()
      });
      Sentry.logger.error('Earphone test error', { error: error, component: 'UnifiedVideoCallSetup' });
    }
  }, [earphoneAttemptCount, resetEarphoneTest, testEarphoneConnection, toast, deviceEnumeration.selectedDevices]);

  const handleEarphoneModalClose = useCallback(() => {
    setShowEarphoneModal(false);
    // Don't reset test state when closing modal - keep error visible inline
    // Only reset when user explicitly starts a new test
  }, []);

  const handleEarphoneTestBypass = useCallback(() => {
    // Allow user to bypass after 2+ failed attempts
    if (earphoneAttemptCount >= 2) {
      console.log('🎧 EARPHONE TEST BYPASSED', {
        attemptCount: earphoneAttemptCount,
        lastStatus: earphoneResult.status,
        lastError: earphoneResult.error,
        timestamp: new Date().toISOString()
      });
      
      setEarphoneTestBypassed(true);
      setEarphoneAcknowledged(true);
      setShowEarphoneModal(false);
      toast({
        title: "Earphone Test Bypassed",
        description: "Proceeding without earphone verification. Please ensure proper audio setup for the best experience.",
        variant: "default",
      });
    }
  }, [earphoneAttemptCount, toast, earphoneResult]);

  const handleGrantPermissions = useCallback(async () => {
    try {
      const permissionsGranted = await handleRetryPermissions();
      if (!permissionsGranted) {
        toast({
          title: "Permissions Required",
          description: "Please grant camera and microphone access, then click 'Test Mic' to verify your microphone",
          variant: "destructive",
        });
        return;
      }

      // After permissions granted, trigger monitor check if required
      if (config.externalMonitorDetection && onRetryMonitorDetection) {
        try {
          const monitorCheckPassed = await onRetryMonitorDetection();
          if (!monitorCheckPassed) {
            toast({
              title: "Monitor Check Required",
              description: "Please disconnect external monitors and try again",
              variant: "destructive",
            });
            return;
          }
        } catch (error) {
          Sentry.logger.error("Error during monitor check", { error: error, component: 'UnifiedVideoCallSetup' });
          toast({
            title: "Monitor Check Error",
            description: "Failed to verify monitor setup. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Success - permissions granted
      toast({
        title: "Permissions Granted",
        description: "Camera and microphone access granted! Please complete all remaining requirements below.",
      });
    } catch (error) {
      console.error('Permission grant flow error:', error);
      toast({
        title: "Setup Error", 
        description: "An error occurred during setup. Please refresh and try again.",
        variant: "destructive",
      });
    }
  }, [handleRetryPermissions, toast, config.externalMonitorDetection, onRetryMonitorDetection]);

  const handleStartCall = useCallback(async () => {
    if (isInterviewSession && !selectedAssistantId && onAssistantSelection) {
      onAssistantSelection();
      return;
    }



    // If permissions are not granted, ONLY request permissions - separate flow

    if (!allPermissionsGranted) {
      await handleGrantPermissions();
      return;
    }

    // For interview sessions with granted permissions, request fullscreen IMMEDIATELY
    if (isInterviewSession && fullscreenSupported && allPermissionsGranted) {
      setRequestingFullscreen(true);
      setFullscreenAttempted(true);
      
      try {
        // Request fullscreen synchronously to preserve user gesture
        const fullscreenSuccess = await requestFullscreen();
        
        if (fullscreenSuccess) {
          // Start interview immediately after fullscreen success
          onStartCall(true);
        } else {
          toast({
            title: "Fullscreen Mode Not Available",
            description: "The interview will continue in windowed mode. For the best experience, please maximize your browser window.",
          });
          onStartCall(true);
        }
      } catch (error) {
        console.error("Fullscreen request error:", error);
        toast({
          title: "Fullscreen Request Failed",
          description: "The interview will continue in windowed mode.",
        });
        onStartCall(true);
      } finally {
        setRequestingFullscreen(false);
      }
    } else {
      // For non-interview sessions or when fullscreen is not supported
      onStartCall(true);
    }
  }, [
    isInterviewSession,
    selectedAssistantId,
    onAssistantSelection,
    allPermissionsGranted,
    handleGrantPermissions,
    onStartCall,
    toast,
    fullscreenSupported,
    requestFullscreen,
  ]);

  // Handle starting interview from the ready screen
  const handleStartInterview = useCallback(async () => {
    if (!selectedAssistantId && onAssistantSelection) {
      onAssistantSelection();
      return;
    }

    // For interview sessions, request fullscreen immediately
    if (isInterviewSession && fullscreenSupported) {
      setRequestingFullscreen(true);
      setFullscreenAttempted(true);
      
      try {
        const fullscreenSuccess = await requestFullscreen();
        
        if (fullscreenSuccess) {
          onStartCall(true);
        } else {
          toast({
            title: "Fullscreen Mode Not Available",
            description: "The interview will continue in windowed mode. For the best experience, please maximize your browser window.",
          });
          onStartCall(true);
        }
      } catch (error) {
        console.error("Fullscreen request error:", error);
        toast({
          title: "Fullscreen Request Failed",
          description: "The interview will continue in windowed mode.",
        });
        onStartCall(true);
      } finally {
        setRequestingFullscreen(false);
      }
    } else {
      // For non-interview sessions
      onStartCall(true);
    }
  }, [
    selectedAssistantId,
    onAssistantSelection,
    isInterviewSession,
    fullscreenSupported,
    requestFullscreen,
    onStartCall,
    toast,
  ]);

  // Enhanced auto-retry fullscreen with multiple attempts for Windows Chrome
  useEffect(() => {
    if (
      isInterviewSession && 
      fullscreenSupported && 
      callActive && 
      !isFullscreen && 
      fullscreenAttempted &&
      !requestingFullscreen &&
      allPermissionsGranted
    ) {
      
      // Multiple retry attempts with increasing delays for Windows Chrome
      const retryAttempts = [500, 1500, 3000]; // 0.5s, 1.5s, 3s
      let currentAttempt = 0;
      
      const attemptFullscreen = async () => {
        try {
          const success = await requestFullscreen();
          if (success) {
            toast({
              title: "Fullscreen Mode Restored",
              description: "Interview continuing in fullscreen mode",
            });
            return true;
          }
        } catch (error) {
          console.error(`Fullscreen retry attempt ${currentAttempt + 1} failed:`, error);
        }
        return false;
      };
      
      const scheduleNextAttempt = () => {
        if (currentAttempt < retryAttempts.length) {
          const delay = retryAttempts[currentAttempt];
          currentAttempt++;
          
          setTimeout(async () => {
            if (!isFullscreen && callActive) { // Check if still needed
              const success = await attemptFullscreen();
              if (!success) {
                scheduleNextAttempt();
              }
            }
          }, delay);
        } else {
        }
      };
      
      scheduleNextAttempt();
    }
  }, [isInterviewSession, fullscreenSupported, callActive, isFullscreen, fullscreenAttempted, requestingFullscreen, requestFullscreen, toast, allPermissionsGranted]);

  const permissions = buildPermissionItems();

  // Show interview ready screen if all permissions are granted for interview sessions
  if (showInterviewReadyScreen && isInterviewSession && allPermissionsGranted) {
    return (
      <InterviewReadyScreen
        lead={lead}
        config={config}
        onStartInterview={handleStartInterview}
        isStartingInterview={requestingFullscreen || preparingCall}
        token={interviewDetails?.token}
        interviewDetails={interviewDetails}
      />
    );
  }

  // Show new unified single screen with new design
  return (
    <div className="force-light min-h-screen bg-gradient-to-tr from-[#f2e9f8] to-white" style={{ fontFamily: 'Satoshi, sans-serif' }}>
      {/* Header */}
      <div className="w-full px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <img
            src="/hirevox.svg"
            alt="HireVox Logo"
            className="h-10"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center px-4 py-16 min-h-[calc(100vh-80px)]">
        {/* Title Section */}
        <div className="text-center mb-8 max-w-5xl">
          <h1 className="text-foreground text-[34px] font-semibold font-general-sans mb-1 whitespace-nowrap">Hello {(() => {
            const leadContactName = interviewDetails?.metadata?.variables?.lead_contact_name;
            const firstName = lead.first_name;
            const emailUser = lead.email?.split('@')[0];
            
            
            const finalName = leadContactName || firstName || emailUser || 'Guest';
            
            return finalName;
          })()}, Let’s Get You Ready for the AI Interview</h1>
          <p className="text-foreground text-xl font-normal font-general-sans">
          Please take a minute to set up your system so everything runs smoothly.
          </p>
        </div>
        
        {/* Permissions Card */}
        <div className="w-full max-w-[650px]">
          <div className="bg-card rounded-xl border border-border p-5">
            {/* Permissions List */}
            <div className="space-y-5">
              {permissions.map((permission, index) => (
                <div key={index}>
                  <div className="flex justify-between items-start pb-2">
                    <div className="flex-1 pr-4">
                      <h3 className="text-base font-medium font-general-sans leading-tight text-foreground">
                        {permission.title}
                      </h3>
                      <p className="text-sm font-normal font-general-sans leading-tight mt-1 text-muted-foreground">
                        {permission.description}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {permission.title.includes("Camera & Microphone") && (
                        <Button
                          onClick={handleTestMicrophone}
                          disabled={micTesting || requestingPermissions}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs bg-card border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl"
                        >
                          {micTesting ? (
                            <>
                              <LoadingSpinner className="w-3 h-3 mr-1" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <Mic className="w-3 h-3 mr-1" />
                              Test Mic
                            </>
                          )}
                        </Button>
                      )}
                      {permission.requiresTest && permission.title.includes("Earphones") && (
                        <Button
                          onClick={handleTestEarphones}
                          disabled={permission.isTesting || requestingPermissions || monitoringState.permissionStatus.camera !== "granted" || (earphoneResult.status === 'earphones_detected' || earphoneTestPassed || earphoneTestBypassed)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs bg-card border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl mr-2"
                        >
                          {permission.isTesting ? (
                            <>
                              <LoadingSpinner className="w-3 h-3 mr-1" />
                              Testing...
                            </>
                          ) : (earphoneResult.status === 'earphones_detected' || earphoneTestPassed) ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </>
                          ) : earphoneTestBypassed ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Bypassed
                            </>
                          ) : earphoneAttemptCount > 0 ? (
                            <>
                              <Headphones className="w-3 h-3 mr-1" />
                              Retest
                            </>
                          ) : (
                            <>
                              <Headphones className="w-3 h-3 mr-1" />
                              Test
                            </>
                          )}
                        </Button>
                      )}
                      {permission.canRetest && speedTestAttempted && (
                        <Button
                          onClick={handleRerunSpeedTest}
                          disabled={speedTestState.isRunning}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs bg-card border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl"
                        >
                          {speedTestState.isRunning ? (
                            <>
                              <LoadingSpinner className="w-3 h-3 mr-1" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <Wifi className="w-3 h-3 mr-1" />
                              Re-test
                            </>
                          )}
                        </Button>
                      )}
                      {permission.requiresCheckbox && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={earphoneAcknowledged}
                              onChange={(e) => setEarphoneAcknowledged(e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`
                              w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                              ${earphoneAcknowledged 
                                ? 'bg-good border-good-line' 
                                : 'bg-card border-border hover:border-border'
                              }
                            `}>
                              {earphoneAcknowledged && (
                                <Check className="w-3 h-3 text-background stroke-[3]" />
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-foreground select-none">
                            Connected
                          </span>
                        </label>
                      )}
                      {!permission.requiresCheckbox && getStatusIcon(
                        permission.status,
                        permission.isError,
                        permission.title.includes("Monitor") ? onRetryMonitorDetection : handleStartCall,
                        permission.title.includes("Camera & Microphone") || permission.title.includes("Earphones") || !!permission.canRetest
                      )}
                    </div>
                  </div>
                  
                  {/* Full-width inline error message */}
                  {permission.inlineError && (
                    <div className="mt-3 p-3 bg-[#FDECEC] rounded-xl">
                      <div className="flex items-start">
                        <div className="flex-1">
                          {typeof permission.inlineError === 'string' ? (
                            <p className="text-sm font-medium text-danger">
                              {permission.inlineError}
                            </p>
                          ) : (
                            permission.inlineError
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {index < permissions.length - 1 && (
                    <div className="border-b border-border/50 mt-2" />
                  )}
                </div>
              ))}
            </div>

            {checkInitialChecksCompleted() && !allPermissionsGranted && getRemainingChecksMessage() && (
              <div className="mt-4 bg-[#F7F7F7] p-2 rounded">
                <p className="text-sm text-[#616161]">
                  <strong className="text-[#616161]">Note:</strong> {getRemainingChecksMessage()}
                </p>
              </div>
            )}
            {/* Allow Access Button */}
            <div className="mt-6">
              <Button
                onClick={handleStartCall}
                disabled={preparingCall || requestingPermissions || requestingFullscreen || (checkInitialChecksCompleted() && !allPermissionsGranted)}
                className="w-full bg-[#8437BB] hover:bg-[#732fa3] text-white font-medium py-3 px-6 rounded-xl text-base h-auto disabled:bg-[#999999] disabled:cursor-not-allowed"
              >
                {preparingCall || requestingPermissions || requestingFullscreen ? (
                  <>
                    <LoadingSpinner className="mr-3" />
                    {requestingFullscreen ? "Entering Fullscreen..." :  
                     requestingPermissions ? "Granting Permissions..." : "Starting..."}
                  </>
                ) : (
                  "Start setup"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Device Selection Section - Only show when tests have actually failed */}
        {(cameraGranted && (
          // Microphone test has been attempted and failed
          (!speechMicResult.isChecking && (speechMicResult.status === 'muted' || speechMicResult.status === 'error')) ||
          // Earphones test has been completed and failed (not while testing)
          (!earphoneResult.isChecking && earphoneAttemptCount > 0 && earphoneResult.status !== 'earphones_detected' && !earphoneTestPassed && !earphoneTestBypassed)
        )) && (
          <div ref={deviceSelectionRef} className="w-full max-w-[650px] mt-6">
            <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-medium text-foreground mb-2 flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Device Selection
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Choose your preferred microphone and speakers for the best experience.
                  </p>
                  <p className="text-xs text-mid bg-mid-bg p-2 rounded">
                    <strong>💡 Note:</strong> If you just connected a new device and don't see it listed, please use the refresh button.
                  </p>
                </div>
                <Button
                  onClick={handleRefreshDevices}
                  disabled={deviceEnumeration.isLoading}
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs bg-card border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl whitespace-nowrap"
                >
                  {deviceEnumeration.isLoading ? (
                    <>
                      <LoadingSpinner className="w-3 h-3 mr-1" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Refresh
                    </>
                  )}
                </Button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Microphone Selection */}
                <DeviceSelector
                  label="Microphone"
                  icon="microphone"
                  devices={deviceEnumeration.devices.audioInputs}
                  selectedDeviceId={deviceEnumeration.selectedDevices.audioDeviceId}
                  onDeviceChange={deviceEnumeration.selectAudioDevice}
                  disabled={deviceEnumeration.isLoading}
                  placeholder="Select microphone..."
                  showTestButton={true}
                  isTestingDevice={deviceEnumeration.isTestingMicrophone}
                  onTestDevice={handleTestSelectedMicrophone}
                  testResult={micTestResult}
                />

                {/* Speaker Selection */}
                <DeviceSelector
                  label="Speakers"
                  icon="speaker"
                  devices={deviceEnumeration.devices.audioOutputs}
                  selectedDeviceId={deviceEnumeration.selectedDevices.audioOutputDeviceId}
                  onDeviceChange={deviceEnumeration.selectAudioOutputDevice}
                  disabled={deviceEnumeration.isLoading}
                  placeholder="Select speakers..."
                  showTestButton={true}
                  isTestingDevice={deviceEnumeration.isTestingSpeaker}
                  onTestDevice={handleTestSpeaker}
                  testResult={speakerTestResult}
                />
              </div>

              {deviceEnumeration.error && (
                <div className="mt-3 p-3 bg-mid-bg border border-mid-line rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="w-4 h-4 text-mid mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-mid">Device Selection Notice</p>
                      <p className="text-sm text-mid mt-1">{deviceEnumeration.error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Messages */}




        


        {/* Earphone Test Modal */}
        {showEarphoneModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card rounded-xl p-6 max-w-lg mx-4 shadow-xl">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center mr-3">
                  <Headphones className="w-6 h-6 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  Earphone Verification Required
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-sm text-foreground mb-4">
                  <strong>Please complete the following setup steps:</strong>
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-foreground text-xs font-bold">1</span>
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">
                        Connect earphones or Bluetooth earbuds
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Wired earphones, wireless earbuds, or over-ear headphones
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-foreground text-xs font-bold">2</span>
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">
                        Ensure correct audio device is selected in OS settings
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Check your system audio settings to confirm output device
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-foreground text-xs font-bold">3</span>
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">
                        Set volume to maximum
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ensure system volume is at 100% for accurate verification
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status/Message Area - Fixed height to prevent modal size changes */}
              <div className="mb-4 min-h-[80px]">
                {/* Test Status Display */}
                {earphoneResult.isChecking && (
                  <div className="bg-secondary border border-border rounded-lg p-3">
                    <div className="flex items-center">
                      <LoadingSpinner className="w-4 h-4 mr-2 text-foreground" />
                      <span className="text-sm text-foreground">
                        {earphoneResult.testPhase === 'setup' && "Setting up verification..."}
                        {earphoneResult.testPhase === 'connecting' && "Connecting to verification service..."}
                        {earphoneResult.testPhase === 'listening' && "Listening for audio feedback..."}
                        {earphoneResult.testPhase === 'playing' && "Playing test audio... (you should hear: 'Testing audio one two three four five')"}
                        {earphoneResult.testPhase === 'analyzing' && "Analyzing results..."}
                        {!earphoneResult.testPhase && "Testing earphones..."}
                      </span>
                    </div>
                  </div>
                )}

                {/* Test Failure Messages */}
                {!earphoneResult.isChecking && earphoneAttemptCount > 0 && earphoneResult.status !== 'earphones_detected' && (
                  <div className="bg-danger-bg border border-danger-line rounded-lg p-3">
                    <div className="flex items-start">
                      <XCircle className="w-4 h-4 text-danger mr-2 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-danger">Verification Failed</p>
                        <p className="text-xs text-danger mt-1">
                          {earphoneResult.status === 'speakers_detected' && "External speakers detected. Please connect earphones and try again."}
                          {earphoneResult.status === 'inconclusive' && "Unable to verify earphone connection. Please check your setup and try again."}
                          {earphoneResult.status === 'error' && (earphoneResult.error?.includes('API key') ? 
                            "Service temporarily unavailable. Please try again." : 
                            "Connection issue. Please check your internet and try again."
                          )}
                        </p>
                        {/* Show retry limit message after 2 attempts */}
                        {earphoneAttemptCount >= 2 && (
                          <p className="text-xs text-danger mt-2 font-medium">
                            Maximum retry attempts reached. Please use "Continue anyway" to proceed.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleEarphoneModalClose}
                  variant="outline"
                  className="flex-1 bg-card border-border text-foreground hover:bg-muted hover:text-foreground"
                  disabled={earphoneResult.isChecking}
                >
                  Cancel
                </Button>
                
                {/* Show Continue anyway button after 2 failed attempts */}
                {earphoneAttemptCount >= 2 && !earphoneResult.isChecking && earphoneResult.status !== 'earphones_detected' && (
                  <Button
                    onClick={handleEarphoneTestBypass}
                    variant="outline"
                    className="flex-1 bg-mid-bg border-mid-line text-mid hover:bg-mid-bg hover:text-mid"
                  >
                    Continue anyway
                  </Button>
                )}
                
                {/* Only show retry button if less than 2 attempts */}
                {earphoneAttemptCount < 2 && (
                  <Button
                    onClick={handleEarphoneModalTest}
                    disabled={earphoneResult.isChecking}
                    className="flex-1 bg-primary hover:bg-primary text-primary-foreground"
                  >
                    {earphoneResult.isChecking ? (
                      <>
                        <LoadingSpinner className="w-4 h-4 mr-2" />
                        Testing...
                      </>
                    ) : earphoneAttemptCount > 0 ? (
                      "Retry Test"
                    ) : (
                      "Start Test"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 
